import { EventType } from "@/analytics/analytics-events";
import { AuthorizeExternalAnnotationProperties } from "@/analytics/generic-events-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectProjectIdForIntegrationType } from "@/store/integrations/integrations-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  Alert,
  DatePicker,
  Dropdown,
  FaroRichTextEditor,
  NoTranslate,
  Option,
} from "@faro-lotv/flat-ui";
import { Analytics, useTranslate } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  ApiResponseError,
  BcfBackendApiErrorDetails,
  BcfServicesIntegrationType,
  GetProjectExtensionsResponse,
  isBcfBackendApiErrorDetails,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Grid } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import {
  dropDownStyle,
  legacyGridItemRowPadding,
  legacyGridSpacing,
} from "./annotation-editor/annotation-editor-meta-fields";
import { SearchableDropdown } from "./annotation-editor/searchable-dropdown";
import {
  CommonExternalAnnotationCreationFormProps,
  ProcoreRfiCompanionFieldId,
  ProcoreRfiCompanionFields,
} from "./annotation-props";
import { ExternalAnnotationEditorHeader } from "./external-annotation-editor-header";
import {
  ExternalAnnotationError,
  isTokenExpirationError,
} from "./external-annotation-error";
import {
  getOptionValues,
  OptionEntry,
  TagWithParent,
} from "./external-annotation-ui-utils";
import { getExternalAnnotationProviderName } from "./external-annotation-utils";
import { HardcodedStatusIds, useHardcodedStatus } from "./use-hardcoded-status";

/** RFI annotations in Procore only support creating drafts. */
const PROCORE_HARDCODED_STATUS_IDS: HardcodedStatusIds = {
  [BcfServicesIntegrationType.procoreRfis]: "draft",
};

type ProcoreAnnotationFormProps = Omit<
  CommonExternalAnnotationCreationFormProps,
  "stage" | "onStageChange"
> &
  ProcoreRfiCompanionFields & {
    /** callback function called when the RFI Manager has changed*/
    onRfiManagerChange(rfiManager: string): void;
  };

/**
 * @returns a partial Procore annotation. This form does not include the title and the annotation type
 * for the complete Procore annotation form, @see ProcoreAnnotation component
 */
export function ProcoreAnnotationForm({
  allowEdition,
  externalAnnotationType,
  title,
  assignee,
  description,
  dueDate,
  status,
  priority,
  topicType,
  rfiManager,
  disabled,
  onTitleChange,
  onAssigneeChange,
  onDescriptionChange,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onTopicTypeChange,
  onRfiManagerChange,
  onError,
  onConnectionTokenExpired,
  onConnectionTokenUpdated,
  showMandatoryFieldsError,
  setAreMandatoryFieldsFilled,
}: ProcoreAnnotationFormProps): JSX.Element {
  const { bcfServicesApiClient } = useApiClientContext();
  const emptyProjectExtensions: GetProjectExtensionsResponse = useMemo(
    () => ({
      topic_type: [],
      topic_status: [],
      topic_label: [],
      priority: [],
      users: [],
      stage: [],
      project_actions: [],
      topic_actions: [],
      comment_actions: [],
      custom_fields: [],
      companion_fields: [],
    }),
    [],
  );

  const descriptionChanged = useCallback(
    (newDescription: string): void => {
      setQuestionText(newDescription);
      onDescriptionChange?.(newDescription);
    },
    [onDescriptionChange],
  );
  const [projectExtensions, setProjectExtensions] =
    useState<GetProjectExtensionsResponse>(emptyProjectExtensions);

  const [tokenExpirationError, setTokenExpirationError] = useState<
    BcfBackendApiErrorDetails | undefined
  >();
  const projectId = useAppSelector(
    selectProjectIdForIntegrationType(externalAnnotationType),
  );
  assert(projectId, "Project ID is required");

  useEffect(() => {
    // eslint-disable-next-line func-style -- FIXME
    const getProjectExtensions = async (): Promise<void> => {
      setProjectExtensions(emptyProjectExtensions);
      try {
        const extensions = await bcfServicesApiClient.getProjectExtensions(
          externalAnnotationType,
          projectId,
        );
        setProjectExtensions(extensions);
      } catch (error) {
        if (error instanceof ApiResponseError) {
          if (isBcfBackendApiErrorDetails(error.body)) {
            if (isTokenExpirationError(error.body)) {
              setTokenExpirationError(error.body);
              onConnectionTokenExpired?.();
            }
          }
        } else {
          onError?.({
            title: "Unable to get the project extensions",
            error,
          });
        }
      }
    };

    if (!tokenExpirationError) {
      getProjectExtensions();
    }
  }, [
    bcfServicesApiClient,
    emptyProjectExtensions,
    setProjectExtensions,
    externalAnnotationType,
    onError,
    setTokenExpirationError,
    onConnectionTokenExpired,
    projectId,
    tokenExpirationError,
  ]);

  const isStatusHardcoded = useHardcodedStatus(
    externalAnnotationType,
    PROCORE_HARDCODED_STATUS_IDS,
    projectExtensions,
    onStatusChange,
  );

  const assigneeOptions = useMemo(
    () =>
      projectExtensions.users.map(
        (user): Option => ({
          key: user.id,
          value: user.id,
          label: <NoTranslate>{user.name ?? user.email}</NoTranslate>,
        }),
      ),
    [projectExtensions],
  );

  const statusOptions = useMemo(
    () =>
      projectExtensions.topic_status.map(
        (status): Option => ({
          key: status.id,
          value: status.id,
          label: <NoTranslate>{status.name}</NoTranslate>,
        }),
      ),
    [projectExtensions],
  );

  const priorityOptions = useMemo(() => {
    const data = projectExtensions.priority.map(
      (item) => new OptionEntry(item),
    );
    return getOptionValues(data);
  }, [projectExtensions]);

  const topicTypeOptions = useMemo(() => {
    const data = projectExtensions.topic_type.map(
      (item) => new OptionEntry(item),
    );
    return getOptionValues(data);
  }, [projectExtensions]);

  const procoreCompanionFields = useMemo(
    () =>
      getProcoreRfiCompanionFieldsValues(projectExtensions.companion_fields),
    [projectExtensions],
  );

  const rfiManagerOptions = useMemo(() => {
    if (!procoreCompanionFields.rfiManagers) {
      return [];
    }
    const data = procoreCompanionFields.rfiManagers.map(
      (item) => new OptionEntry(item),
    );
    return getOptionValues(data);
  }, [procoreCompanionFields]);

  const [titleErrorMessage, setTitleErrorMessage] = useState<
    string | undefined
  >();

  const [questionText, setQuestionText] = useState<string | undefined>();

  const [enableRfiManagerErrorIndication, setEnableRfiManagerErrorIndication] =
    useState(false);
  const [enableQuestionError, setEnableQuestionError] = useState(false);
  const [enableStatusError, setEnableStatusError] = useState(false);
  const [enableTypeError, setEnableTypeError] = useState(false);

  // Reset errors and title message when annotation type changes
  useEffect(() => {
    setEnableRfiManagerErrorIndication(false);
    setEnableStatusError(false);
    setEnableQuestionError(false);
    setEnableTypeError(false);
    setTitleErrorMessage(undefined);
  }, [externalAnnotationType]);

  // validate mandatory fields`
  useEffect(() => {
    const validRfiManager =
      externalAnnotationType === BcfServicesIntegrationType.procoreRfis
        ? !!rfiManager
        : true;

    const validQuestion =
      externalAnnotationType === BcfServicesIntegrationType.procoreRfis
        ? (questionText ?? "").trim().length > 0
        : true;

    const validTopicType =
      externalAnnotationType === BcfServicesIntegrationType.procoreObservations
        ? !!topicType
        : true;

    setEnableRfiManagerErrorIndication(
      showMandatoryFieldsError && !validRfiManager,
    );
    setEnableStatusError(showMandatoryFieldsError && !status);
    setEnableQuestionError(showMandatoryFieldsError && !validQuestion);
    setEnableTypeError(showMandatoryFieldsError && !validTopicType);

    setAreMandatoryFieldsFilled(
      !titleErrorMessage && validQuestion && !!status && validRfiManager,
    );
  }, [
    externalAnnotationType,
    questionText,
    rfiManager,
    setAreMandatoryFieldsFilled,
    showMandatoryFieldsError,
    status,
    titleErrorMessage,
    topicType,
  ]);

  const { handleErrorWithToast } = useErrorHandlers();

  // Callback to invoke when the token of the external annotation has been updated
  const onTokenUpdated = useCallback(() => {
    setTokenExpirationError(undefined);
    onConnectionTokenUpdated?.();
  }, [onConnectionTokenUpdated]);

  const translate = useTranslate();

  return (
    <>
      {tokenExpirationError && (
        <Alert
          variant="error"
          title="Connection Error"
          description={
            <ExternalAnnotationError
              integrationType={externalAnnotationType}
              errorDetails={tokenExpirationError}
              onTokenUpdated={onTokenUpdated}
              onAuthorizationButtonClicked={() =>
                Analytics.track<AuthorizeExternalAnnotationProperties>(
                  EventType.authorizeExternalAnnotation,
                  {
                    via: "creation dialog",
                    service: getExternalAnnotationProviderName(
                      externalAnnotationType,
                    ),
                  },
                )
              }
            />
          }
        />
      )}
      <ExternalAnnotationEditorHeader
        title={title}
        annotationType={externalAnnotationType}
        autoFocusTitle
        onTitleChange={onTitleChange}
        errorMessage={titleErrorMessage}
        onSetErrorMessage={setTitleErrorMessage}
        disabled={!allowEdition || !!disabled}
      />
      <Stack
        sx={{
          // Add a scrollbar only to the main content if needed
          overflow: "auto",
          flexGrow: 1,
          gap: 3,
        }}
      >
        <FaroRichTextEditor
          initialText={description}
          dark
          fullWidth
          readOnly={!allowEdition || !!disabled}
          label={
            externalAnnotationType === BcfServicesIntegrationType.procoreRfis
              ? "Question"
              : "Description"
          }
          tag={
            externalAnnotationType === BcfServicesIntegrationType.procoreRfis
              ? "star"
              : undefined
          }
          placeholder={translate(
            `Insert a ${externalAnnotationType === BcfServicesIntegrationType.procoreRfis ? "question" : "description"}`,
          )}
          onError={(error) =>
            handleErrorWithToast({ title: "Error in description", error })
          }
          error={
            enableQuestionError &&
            externalAnnotationType === BcfServicesIntegrationType.procoreRfis
              ? "Question is required"
              : undefined
          }
          onChange={descriptionChanged}
          onBlur={() =>
            setEnableQuestionError(
              externalAnnotationType ===
                BcfServicesIntegrationType.procoreRfis &&
                (!questionText || questionText.trim().length === 0),
            )
          }
          sx={{
            height: "120px",
          }}
        />
        <Grid container {...legacyGridSpacing}>
          <Grid size={6}>
            <Dropdown
              aria-label="Procore annotation status dropdown"
              label="Status"
              tag="star"
              disabled={
                !allowEdition ||
                statusOptions.length === 0 ||
                isStatusHardcoded ||
                !!disabled
              }
              value={status}
              options={statusOptions}
              dark
              fullWidth
              sx={dropDownStyle}
              error={enableStatusError}
              onClose={() => setEnableStatusError(!status)}
              onChange={
                onStatusChange === undefined
                  ? undefined
                  : (event) => onStatusChange(event.target.value)
              }
              shouldCapitalize={false}
            />
          </Grid>
          {externalAnnotationType ===
            BcfServicesIntegrationType.procoreObservations && (
            <>
              <Grid size={6}>
                <Dropdown
                  aria-label="Procore annotation type dropdown"
                  label="Type"
                  tag="star"
                  disabled={
                    !allowEdition || topicTypeOptions.length === 0 || !!disabled
                  }
                  value={topicType}
                  options={topicTypeOptions}
                  dark
                  fullWidth
                  sx={dropDownStyle}
                  error={enableTypeError}
                  onClose={() => setEnableTypeError(!topicType)}
                  onChange={
                    onTopicTypeChange === undefined
                      ? undefined
                      : (event) => onTopicTypeChange(event.target.value)
                  }
                  shouldCapitalize={false}
                />
              </Grid>
              <Grid size={6}>
                <SearchableDropdown
                  selectedOption={assignee}
                  options={assigneeOptions}
                  label="Assignee"
                  onOptionChange={onAssigneeChange}
                  disabled={
                    !allowEdition || assigneeOptions.length === 0 || !!disabled
                  }
                />
              </Grid>
              <Grid size={6}>
                <Dropdown
                  aria-label="Procore annotation priority dropdown"
                  label="Priority"
                  disabled={
                    !allowEdition || priorityOptions.length === 0 || !!disabled
                  }
                  value={priority}
                  options={priorityOptions}
                  dark
                  fullWidth
                  sx={dropDownStyle}
                  onChange={
                    onPriorityChange === undefined
                      ? undefined
                      : (event) => onPriorityChange(event.target.value)
                  }
                  shouldCapitalize={false}
                />
              </Grid>
            </>
          )}
          {externalAnnotationType ===
            BcfServicesIntegrationType.procoreRfis && (
            <Grid size={6}>
              <SearchableDropdown
                selectedOption={rfiManager}
                options={rfiManagerOptions}
                label="RFI Manager"
                tag="star"
                error={enableRfiManagerErrorIndication}
                onOptionChange={(s) => onRfiManagerChange(s ?? "")}
                disabled={
                  !allowEdition || rfiManagerOptions.length === 0 || !!disabled
                }
                onClose={() => setEnableRfiManagerErrorIndication(!rfiManager)}
              />
            </Grid>
          )}
          {externalAnnotationType ===
            BcfServicesIntegrationType.procoreObservations && (
            <Grid size={6} sx={legacyGridItemRowPadding}>
              <DatePicker
                label="Due date"
                disabled={!allowEdition || !!disabled}
                initialDate={dueDate ?? new Date()}
                variant="Dark"
                formSx={{ width: "100%" }}
                onChange={
                  onDueDateChange === undefined
                    ? undefined
                    : (date) => (date ? onDueDateChange(date) : {})
                }
                isClearable
              />
            </Grid>
          )}
        </Grid>
      </Stack>
    </>
  );
}

/**
 * Stores all the possible values for Procore RFI Companion fields.
 */
type ProcoreRfiCompanionFieldsValues = {
  /** The values for RFI Manager field */
  rfiManagers?: TagWithParent[];
};

/**
 * Extracts the values of the Procore RFI companion fields from the project extensions
 *
 * @param companionFields the companion fields from the project extensions
 * @returns the values of the Procore RFI companion fields
 */
function getProcoreRfiCompanionFieldsValues(
  companionFields: GetProjectExtensionsResponse["companion_fields"],
): ProcoreRfiCompanionFieldsValues {
  let rfiManagers: TagWithParent[] | undefined = undefined;
  for (const field of companionFields) {
    if (field.id === ProcoreRfiCompanionFieldId.rfiManager) {
      rfiManagers = field.enumValues?.map((value) => ({
        id: value.value,
        name: value.displayValue,
        parentTagName: value.parentValue,
        disabled: value.readonly,
      }));
    }
  }
  return { rfiManagers };
}
